// ======================== React & Style =========================
import React, { memo, useCallback, useEffect, useRef, useState } from 'react';
// ======================== REDUX =================================
import { useDispatch, useSelector } from 'react-redux';
import {
  setMentionIdAction,
  setReplyMessageIdAction,
} from '../../../redux/actions/collaboration';
// ======================== Apollo ================================
import { useApolloClient, useMutation } from '@apollo/client';
import preloadUrlQuery from '../apollo/preloadUrlQuery';
import messageCreateMutation from './apollo/messageCreateMutation';
// ======================== Component =============================
import { Form } from 'semantic-ui-react';
import KubitTooltip from '../../KubitTooltip';
import { EditorState } from 'draft-js';
import TextInputArea from '../../TextInputArea';
import CropScreenshot from '../CropScreenshot';
// ======================== Utils =================================
import getRedirectBaseUrl from '../../../utils/getRedirectBaseUrl';
import axios from 'axios';
import {
  clearTextInput,
  editorDecorator,
  formatEditorState,
  getMentionedUsers,
} from '../utils';

import * as MessageSubmitUI from './MessageSubmitUI';
import { useAppSelector } from '../../../redux/hooks';
import { workspacePropertySelector } from '../../../redux/selectors/workspace';
import toastMessage from '../../../utils/toastMessage';
import {
  IMessageCreateMutation,
  IMessageCreateMutationVariables,
} from '../../../utils/apollo/graphqlSchema';

export default memo(function MessageSubmit() {
  const dispatch = useDispatch();
  const client = useApolloClient();

  const workspaceId = useAppSelector((state) =>
    workspacePropertySelector(state, 'id'),
  );

  const mainChannelId = useSelector(
    (state: any) => state.collaboration.mainChannelId,
  );
  const currentChannelId = useSelector(
    (state: any) => state.collaboration.currentChannelId,
  );
  const activeTag = useSelector((state: any) => state.collaboration.activeTag);
  const userList = useSelector((state: any) => state.collaboration.userList);

  const replyMessageId = useSelector(
    (state: any) => state.collaboration.replyMessageId,
  );
  const mentionId = useSelector((state: any) => state.collaboration.mentionId);

  const setMentionId = useCallback(
    (newMentionId: string) => {
      dispatch(setMentionIdAction(newMentionId));
    },
    [dispatch],
  );

  const [editorState, setEditorState] = useState(
    EditorState.createEmpty(editorDecorator({})),
  );
  const editorContent = editorState.getCurrentContent();
  const plainMessage = editorContent.getPlainText().trim();

  /* Prepare createMessage mutation variables */
  const input: any = {
    channelId: currentChannelId,
    text: plainMessage,
    key: '',
    parent: replyMessageId ? replyMessageId : '',
    metadata: {
      landingUrl: getRedirectBaseUrl(),
    },
  };

  const [createMessage] = useMutation<
    IMessageCreateMutation,
    IMessageCreateMutationVariables
  >(messageCreateMutation, {
    variables: { input },
    onError: () => {
      errorOnSubmitMessage();
    },
  });

  // mention list to check the final mentioned users
  const [mentionedUsers, setMentionedUsers] = useState([]);
  // set preload image data for upload
  const [preloadData, setPreloadData] = useState<any>({});
  // recording the cursor position
  const [selectorPos, setSelectorPos] = useState(0);
  // post createMessage mutation.
  const confirmMutation = async (postMutation: () => void) => {
    // if input is invalid, return.
    if (
      ((plainMessage.length > 4000 || plainMessage.length === 0) &&
        preloadData.objectKey == null) ||
      activeTag !== ''
    ) {
      return;
    }

    input.formatted = formatEditorState(editorState);

    // find final mention list by using plain message.
    const newMentionList = getMentionedUsers(
      userList,
      mentionedUsers,
      plainMessage,
    );

    // if it has mention blocks
    if (newMentionList.length > 0) {
      input.metadata = {
        mentions: newMentionList,
        landingUrl: getRedirectBaseUrl(),
      };
    }
    setSelectorPos(0);
    // if image exist, convert it to data64, then post mutation
    if (preloadData.objectKey && preloadData.image) {
      const fileReader: any = new FileReader();
      fileReader.readAsDataURL(preloadData.image);
      input.key = preloadData.objectKey;
      fileReader.onloadend = () => {
        axios.put(preloadData.url, { name: fileReader.result });
      };
      postMutation();
    } else {
      postMutation();
    }
    // reset all variables
    dispatch(setReplyMessageIdAction(''));
    setPreloadData({});
    setEditorState(clearTextInput(editorState));
    setMentionedUsers([]);
  };

  // if switch channel, reset all variables.
  useEffect(() => {
    setEditorState(clearTextInput(editorState));
    setMentionedUsers([]);
    setSelectorPos(0);
    dispatch(setReplyMessageIdAction(''));
    setPreloadData({});
    // eslint-disable-next-line
  }, [currentChannelId]);

  // upload image
  const uploadElement = useRef<HTMLInputElement>(null);
  const imageUploadHandler = () => {
    if (uploadElement.current && activeTag === '') {
      uploadElement.current.click();
    }
  };

  // validate image format and size
  const isImageFile = (file: any) => {
    // if file is null or undefined
    if (file == null) {
      return false;
    }
    // using simple check for now. will check mime real type in future
    if (file.type.substring(0, 5) !== 'image') {
      return false;
    }

    // if file larger than 5mb
    if (file.size > 5 * 1024 * 1024) {
      return false;
    }

    return true;
  };

  // upload image to text editor.
  const uploadImageChange = async (e: any) => {
    e.persist();
    const preloadResponse = await client.query({
      query: preloadUrlQuery,
      variables: {
        id: workspaceId,
        kind: 'IMAGE',
      },
    });
    if (e.target && e.target.files) {
      if (!isImageFile(e.target.files[0])) {
        // call toast to remind user for the size and type of uploaded file.
        toastMessage({
          message: 'The maximum image file size for upload is 5 MB.',
          type: 'error',
        });
        setPreloadData({});
        return;
      }
      const newPreload: any = {};
      newPreload.objectKey = preloadResponse.data.preUpload.objectKey;
      newPreload.url = preloadResponse.data.preUpload.url;
      newPreload.image = e.target.files[0];
      setPreloadData(newPreload);
      e.target.value = '';
    }
  };

  const errorOnSubmitMessage = () => {
    toastMessage({
      message: 'Failed to send message.  Try again.',
      type: 'error',
    });
  };

  const uploadButtonColor = () => {
    if (activeTag !== '') {
      return '#cccccc';
    }

    return preloadData.objectKey ? 'blue' : 'grey';
  };

  return (
    <MessageSubmitUI.MessageBar id={'message-submit'}>
      <>
        <Form>
          {/* text editor */}
          <TextInputArea
            height={'132px'}
            minHeight={'44px'}
            editorState={editorState}
            setEditorState={setEditorState}
            postMutation={() => createMessage()}
            confirmMutation={confirmMutation}
            userList={userList}
            isMainChannel={mainChannelId === currentChannelId}
            mentionedUsers={mentionedUsers}
            setMentionedUsers={setMentionedUsers}
            mentionId={mentionId}
            setMentionId={setMentionId}
            selectorPos={selectorPos}
            setSelectorPos={setSelectorPos}
            activeTag={activeTag}
          />

          <MessageSubmitUI.MessageTypeMenu>
            {/* upload image  */}
            <MessageSubmitUI.MessageControl>
              <KubitTooltip
                placement="top-start"
                content={
                  activeTag === ''
                    ? 'Image Size <= 5 MB.'
                    : 'Cannot upload image in filter channel.'
                }
                noOpenDelay={true}
              >
                <MessageSubmitUI.UploadIconContainer>
                  <MessageSubmitUI.ControlIcon
                    name="picture"
                    defaultValue={uploadButtonColor()}
                    onClick={() => imageUploadHandler()}
                  />
                  <MessageSubmitUI.HiddenUploadButton
                    hidden
                    ref={uploadElement}
                    type="file"
                    accept="image/*"
                    onChange={(e: any) => uploadImageChange(e)}
                  />
                </MessageSubmitUI.UploadIconContainer>
              </KubitTooltip>
              <CropScreenshot hasContainer={false} updateZIndices={true} />
            </MessageSubmitUI.MessageControl>
            {/* submit and cancel buttons for different cases */}
            <MessageSubmitUI.ButtonGroups>
              {(replyMessageId || preloadData.objectKey) && (
                <MessageSubmitUI.MessageCancelButton
                  onClick={() => {
                    setPreloadData({});
                    setEditorState(clearTextInput(editorState));
                    dispatch(setReplyMessageIdAction(''));
                  }}
                >
                  Clear
                </MessageSubmitUI.MessageCancelButton>
              )}
              {/* submit button for reply and send message */}
              <MessageSubmitUI.MessageSubmitButton
                defaultChecked={
                  ((plainMessage.length > 4000 ||
                    plainMessage.trim().length === 0) &&
                    preloadData.objectKey == null) ||
                  activeTag !== ''
                    ? false
                    : true
                }
                defaultValue={replyMessageId}
                onClick={() => confirmMutation(createMessage)}
              >
                {replyMessageId ? 'Reply' : 'Send'}
              </MessageSubmitUI.MessageSubmitButton>
            </MessageSubmitUI.ButtonGroups>
          </MessageSubmitUI.MessageTypeMenu>
        </Form>
      </>
    </MessageSubmitUI.MessageBar>
  );
});
